"use client"

import { Box, Grid, GridItem, Text, Flex } from "@chakra-ui/react"
import InterviewDetailsCard from "services/Recruit/components/RecruitCards/TodaysInterviews"
import { getApi } from "common/services/api"
import { API_PATHS } from "common/services/apiPaths"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import { rejectedStatuses } from "services/Recruit/constant"
import { constant } from "common/constant"
import { selectedStatuses } from "services/Recruit/constant"
import {
  MdAutoAwesome,
  MdVisibility,
  MdCalendarToday,
  MdAccessTime,
  MdCheckCircleOutline,
  MdStarOutline,
  MdPeopleOutline,
  MdChevronRight
} from "react-icons/md"
import {
  Badge,
  VStack,
  HStack,
  Avatar,
  Button,
  Icon,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react"
import { ChevronDownIcon } from "@chakra-ui/icons"
import CustomDatePicker from "common/components/datePicker/CustomDatePicker"
import { getLoggedInUser, isActiveRoleEmployee } from "common/utils/roleUtils"

export default function RecruitHomePage() {
  const [data, setData] = useState([])
  const [hasDirectReports, setHasDirectReports] = useState(false)
  const [dateFilter, setDateFilter] = useState("all_time")
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const [dateError, setDateError] = useState("")
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const DASHBOARD_DATE_STORAGE_KEY = "dashboardDateFilter"

  // Detect employee role from localStorage user (once on mount)
  useEffect(() => {
    try {
      const u = localStorage.getItem("user")
      setUser(u ? JSON.parse(u) : null)
    } catch (e) {
      setUser(null)
    }
  }, [])

  // Restore dashboard date filter from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DASHBOARD_DATE_STORAGE_KEY)
      if (!stored) return
      const parsed = JSON.parse(stored)
      if (parsed?.dateFilter) {
        setDateFilter(parsed.dateFilter)
      }
      if (parsed?.customFrom) {
        setCustomFrom(parsed.customFrom)
      }
      if (parsed?.customTo) {
        setCustomTo(parsed.customTo)
      }
    } catch (e) {
      // ignore parsing errors
    }
  }, [])

  // Helper to get active role name
  const getActiveRoleName = (user) => {
    if (!user) return null;

    // Check if activeRoleId is populated (has roleName property)
    let role = user?.activeRoleId?.roleName;

    // If activeRoleId is just an ID, look it up in roles array
    if (!role && user?.activeRoleId && user?.roles) {
      const activeRoleId = typeof user.activeRoleId === 'string'
        ? user.activeRoleId
        : user.activeRoleId._id || user.activeRoleId;
      const activeRole = user.roles.find((r) => {
        const roleId = typeof r === 'object' ? (r._id || r) : r;
        return roleId?.toString() === activeRoleId?.toString();
      });
      role = activeRole?.roleName;
    }

    // Fallback to first role or user.role for old users
    if (!role) {
      role = user?.roles?.[0]?.roleName || user?.role;
    }

    return role;
  };

  const activeRole = getActiveRoleName(user);
  const normalizedActiveRole = activeRole?.toLowerCase();
  const isEmployee = normalizedActiveRole === "employee";
  const isAdminOrRecruiter = ["admin", "superadmin", "recruiter", "hiringmanager"].includes(normalizedActiveRole);
  const userId = user?._id || user?.id
  const userDisplayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.name ||
    user?.fullName ||
    user?.email ||
    "User"

  const brandColor = "#3C2FC0";
  const dashboardCardIconSize = 7; // Chakra size token = 28px
  const bgPerformanceCard = useColorModeValue("white", "navy.800");
  const performanceBorderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const performanceTextColor = useColorModeValue("navy.700", "white");
  const performanceTextColorSecondary = "gray.500";

  const dateFilterOptions = [
    { value: "all_time", label: "All" },
    { value: "current_week", label: "Current Week" },
    { value: "current_month", label: "Current Month" },
    { value: "financial_year", label: "Financial Year" },
    { value: "custom", label: "Custom" },
  ];

  const toDateInput = (dateObj) => {
    // Use local date parts (not `toISOString`) to avoid timezone shifting (e.g., 1st showing as 31st).
    if (!dateObj) return "";
    const d = new Date(dateObj);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const ymdToDate = (value) => {
    if (!value) return null
    const s = String(value)
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
    if (!m) return null
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }

  const clearDashboardDateFilter = () => {
    setDateFilter("all_time")
    setCustomFrom("")
    setCustomTo("")
    setDateError("")
  }

  const getDateRangeForFilter = () => {
    if (dateFilter === "all_time") {
      return null;
    }

    if (dateFilter === "custom") {
      if (!customFrom || !customTo) return null;
      if (customFrom > customTo) return null;
      return { startDate: customFrom, endDate: customTo };
    }

    const now = new Date();
    if (dateFilter === "current_week") {
      const day = now.getDay();
      const diffToMonday = (day + 6) % 7;
      const start = new Date(now);
      start.setDate(now.getDate() - diffToMonday);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { startDate: toDateInput(start), endDate: toDateInput(end) };
    }

    if (dateFilter === "current_month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      return { startDate: toDateInput(start), endDate: toDateInput(end) };
    }

    if (dateFilter === "financial_year") {
      const month = now.getMonth();
      const year = now.getFullYear();
      // FY in India: Apr 1 -> Mar 31. If we're in Jan/Feb/Mar, FY started last year.
      const startYear = month >= 3 ? year : year - 1;
      const start = new Date(startYear, 3, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(startYear + 1, 2, 31);
      end.setHours(23, 59, 59, 999);
      return { startDate: toDateInput(start), endDate: toDateInput(end) };
    }

    return null;
  };

  const dateRange = getDateRangeForFilter();
  const dateFilterLabel = dateFilterOptions.find((opt) => opt.value === dateFilter)?.label || "Date Filter";
  const formatDisplayDate = (ymd) => {
    if (!ymd) return "";
    const parts = ymd.split("-");
    if (parts.length !== 3) return ymd;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const dateRangeLabel = dateRange ? `${formatDisplayDate(dateRange.startDate)} to ${formatDisplayDate(dateRange.endDate)}` : "";

  useEffect(() => {
    if (dateFilter !== "custom") {
      setDateError("");
      return;
    }
    if (!customFrom || !customTo) {
      setDateError("");
      return;
    }
    if (customFrom > customTo) {
      setDateError("From date cannot be after To date");
    } else {
      setDateError("");
    }
  }, [dateFilter, customFrom, customTo]);

  // Persist dashboard date filter to localStorage
  useEffect(() => {
    try {
      const payload = {
        dateFilter,
        customFrom,
        customTo,
      }
      localStorage.setItem(DASHBOARD_DATE_STORAGE_KEY, JSON.stringify(payload))
    } catch (e) {
      // ignore storage errors
    }
  }, [dateFilter, customFrom, customTo])

  // Redirect reviewers to document verification instead of dashboard
  useEffect(() => {
    if (normalizedActiveRole === "reviewer") {
      navigate("/recruitment/document-verification", { replace: true })
    }
  }, [normalizedActiveRole, navigate])

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const params = new URLSearchParams();
        if (dateFilter && dateFilter !== "all_time") {
          params.set("dateFilter", dateFilter);
        }
        if (dateRange) {
          params.set("startDate", dateRange.startDate);
          params.set("endDate", dateRange.endDate);
        }

        const url = params.toString()
          ? `${API_PATHS.RECRUITMENT_DASHBOARD}?${params.toString()}`
          : API_PATHS.RECRUITMENT_DASHBOARD;

        const result = await getApi(url)
        setData(result?.data || {})
      } catch (e) {
        toast.error(constant.somethingWrong)
      }
    }

    if (!user) return;
    if (dateFilter === "custom" && (!customFrom || !customTo)) return;
    if (dateError) return;

    fetchDashboardData()
  }, [user, dateFilter, customFrom, customTo, dateError])

  useEffect(() => {
    let mounted = true

    const checkDirectReports = async () => {
      const isEmployeeUser = isActiveRoleEmployee(getLoggedInUser())
      if (isEmployeeUser || !userId) {
        if (mounted) setHasDirectReports(false)
        return
      }

      try {
        const response = await getApi("api/user?allData=true")
        const users = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : []

        const currentUserId = String(userId)
        const isManagerOfAnyUser = users.some((u) => {
          const reportingManagerId = String(
            u?.managerId?._id ||
            u?.managerId ||
            ""
          )
          return reportingManagerId === currentUserId
        })

        if (mounted) setHasDirectReports(isManagerOfAnyUser)
      } catch (error) {
        if (mounted) setHasDirectReports(false)
      }
    }

    checkDirectReports()

    return () => {
      mounted = false
    }
  }, [isEmployee, userId])

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDateString = () => {
    const today = new Date()
    return today.toISOString().split("T")[0]
  }

  const appendDateFilterParams = (params) => {
    if (dateFilter && dateFilter !== "all_time") {
      params.set("dateFilter", dateFilter);
    }
    if (dateRange) {
      params.set("startDate", dateRange.startDate);
      params.set("endDate", dateRange.endDate);
    }
  };

  const buildUrlWithParams = (path, extraParams = {}) => {
    const [basePath, existingQuery] = path.split("?");
    const params = new URLSearchParams(existingQuery || "");

    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });

    appendDateFilterParams(params);
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
  };

  // Navigation handler for different card types
  const handleCardClick = (cardId, title) => {
    const hasDateRange = Boolean(dateRange && dateFilter !== "all_time");

    switch (cardId) {
      case 1: // Total Openings
        navigate(buildUrlWithParams("/recruitment/jobProfile"))
        break
      case 2: // Shortlisted Candidate / Employee: Today's Interviews
        if (isEmployee) {
          if (hasDateRange) {
            navigate(buildUrlWithParams("/recruitment/interviews"))
          } else {
            const todaysDate = getTodayDateString()
            navigate(buildUrlWithParams("/recruitment/interviews", { interviewDate: todaysDate }))
          }
        } else {
          // For non-employees, navigate to applications with shortlisted filter
          navigate(buildUrlWithParams("/recruitment/applications", { status: "shortlisted" }))
        }
        break
      case 3: // Total Hired
        navigate(buildUrlWithParams("/recruitment/applications", { status: "hired" }))
        break
      case 4: // Total Rejected
        navigate(buildUrlWithParams("/recruitment/applications", { status: "total_rejected" }))
        break
      case 5: // New Applications
        if (hasDateRange) {
          navigate(buildUrlWithParams("/recruitment/applications"))
        } else {
          const todayDate = getTodayDateString()
          navigate(buildUrlWithParams("/recruitment/applications", { createdDate: todayDate }))
        }
        break
      case 6: // Shortlisted Candidates
        navigate(buildUrlWithParams("/recruitment/applications", { status: "shortlisted" }))
        break
      case 7: // Today's Interviews
        if (hasDateRange) {
          navigate(buildUrlWithParams("/recruitment/interviews"))
        } else {
          const todaysDate = getTodayDateString()
          navigate(buildUrlWithParams("/recruitment/interviews", { interviewDate: todaysDate }))
        }
        break
      case 8: // Total Interviews Open (employee-specific)
        navigate(buildUrlWithParams("/recruitment/interviews"))
        break
      default:
        console.log("No navigation defined for this card")
    }
  }

  // Default cards (non-employee)
  const DefaultCardData = [
    {
      id: 1,
      title: "Total Openings",
      value: data?.totalOpenings,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Shortlisted Candidate",
      value: data?.shortlistedCandidates,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Total Hired",
      value: data?.totalHired,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.5">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <polyline points="16 11 18 13 22 9" />
        </svg>
      ),
    },
    {
      id: 4,
      title: "Total Rejected",
      value: data?.totalRejected,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="m15 9-6 6" />
          <path d="m9 9 6 6" />
        </svg>
      ),
    },
  ]

  // Employee-specific cards
  const EmployeeCardData = [
    DefaultCardData[0],
    {
      id: 2,
      title: "Today's Interviews",
      value: data?.todaysInterviews,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
          <path d="M18 21a6 6 0 0 0-12 0" />
          <circle cx="12" cy="11" r="4" />
          <rect width="18" height="18" x="3" y="3" rx="2" />
        </svg>
      ),
    },
    {
      id: 8,
      title: "Total Interviews",
      value: data?.totalInterviewsForUser || 0,
      image: (
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
          <circle cx="12" cy="8" r="4" />
          <path d="M2 21a10 10 0 0 1 20 0" />
        </svg>
      ),
    },
  ]

  const FigmaDisplayCards = DefaultCardData.slice(0, 4)

  const perf = data?.performance || {};
  const isReportingManager = Boolean(
    perf.isReportingManager ||
    (perf.directReportCount ?? 0) > 0 ||
    hasDirectReports
  );

  const performanceStats = [
    { title: "Active Cycles", value: perf.activeCycles ?? '-', icon: MdCalendarToday, iconColor: "blue.500", iconBg: "blue.50", onClick: () => navigate(buildUrlWithParams('/performance/cycles', { status: "active" })) },
    { title: "Pending Reviews", value: perf.pendingReviews ?? '-', icon: MdAccessTime, iconColor: "orange.500", iconBg: "orange.50", onClick: () => navigate(buildUrlWithParams('/performance/approvals', { reviewCycle: 'all', status: 'pending' })) },
    { title: "Completed Cycles", value: perf.completedReviews ?? '-', icon: MdCheckCircleOutline, iconColor: "green.500", iconBg: "green.50", onClick: () => navigate(buildUrlWithParams('/performance/cycles', { status: "completed" })) },
    {
      title: "Avg Score",
      value: perf.avgScore != null ? `${perf.avgScore} / 5` : '-',
      icon: MdStarOutline,
      iconColor: "purple.500",
      iconBg: "purple.50",
      onClick: () => navigate(buildUrlWithParams('/performance/analytics', {
        cycleId: perf.avgScoreCycleId,
      }))
    },
  ];

  const employeePerformanceCards = [
    {
      title: "Current Performance Score",
      value: perf.myScore != null ? `${Number(perf.myScore).toFixed(1)} / 5` : '-',
      desc: perf.myScoreSource === 'pending'
        ? 'Awaiting Manager Rating'
        : perf.myPerformanceCategory
          ? `${perf.myPerformanceCategory.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}  ${perf.myScoreSource === 'manager' ? '' : '- Self'}`
          : 'No score yet',
      valueColor: "#00A3FF",
      icon: MdStarOutline,
    },
    {
      title: "Pending Reviews",
      value: perf.myPendingSelfReviews ?? '-',
      desc: `${perf.myPendingSelfReviews ?? 0} Self Reviews Pending`,
      valueColor: "#FF9900",
      icon: MdAccessTime,
      onClick: () => navigate(buildUrlWithParams('/performance/my-reviews', { status: "pending" })),
    },
    ...(isReportingManager ? [{
      title: "Team Reviews",
      value: perf.myPendingTeamReviews ?? '-',
      desc: `${perf.myPendingTeamReviews ?? 0} Pending`,
      valueColor: "#F539B5",
      icon: MdPeopleOutline,
      onClick: () => navigate(buildUrlWithParams('/performance/team-reviews', { status: "pending" })),
    }] : []),
  ];

  const employeeHiringCards = [
    {
      id: 1,
      title: "Open Positions Involved In",
      value: data?.totalOpenings ?? 0,
      desc: "Shared open positions",
      image: EmployeeCardData[0].image,
      onClick: () => navigate(buildUrlWithParams("/recruitment/jobProfile")),
    },
    {
      id: 7,
      title: "Upcoming Interviews",
      value: data?.todaysInterviews ?? 0,
      desc: "Scheduled for today",
      image: EmployeeCardData[1].image,
      onClick: () => {
        if (dateRange && dateFilter !== "all_time") {
          navigate(buildUrlWithParams("/recruitment/interviews"));
        } else {
          const todaysDate = getTodayDateString();
          navigate(buildUrlWithParams("/recruitment/interviews", { interviewDate: todaysDate }));
        }
      },
    },
    {
      id: 8,
      title: "Total Interviews Conducted",
      value: data?.totalInterviewsForUser || 0,
      desc: "All-time interviews",
      image: EmployeeCardData[2].image,
      onClick: () => navigate(buildUrlWithParams("/recruitment/interviews")),
    },
  ];
  const recentReviews = perf.recentReviews || [];

  // Generate avatar background colors based on employee name
  const getAvatarColor = (name) => {
    const colors = ['purple.500', 'blue.500', 'pink.500', 'green.500', 'orange.500', 'teal.500'];
    const index = name ? name.length % colors.length : 0;
    return colors[index];
  };

  // Format status for display
  const formatStatus = (status) => {
    if (status === 'submitted') return 'Submitted';
    if (status === 'approved') return 'Approved';
    return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (isEmployee) {
    return (
      <Box py={3} px={6} bg="#F9FAFB" minH="100vh">
        <VStack spacing={6} align="stretch">
          <Flex align="center" justify="space-between" flexWrap="wrap" mt={2} gap={3}>
            <Text fontSize="2xl" fontWeight="700" color="#1B2559">
              {`Welcome, ${userDisplayName} 👋`}
            </Text>
            <Box>
              <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
                <Menu closeOnSelect>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" size="sm">
                    {dateFilterLabel}
                  </MenuButton>
                  <MenuList p={2} minW="240px">
                    {dateFilterOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={() => setDateFilter(option.value)}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {dateFilter === "custom" ? (
                  <>
                    <Box w="170px">
                      <CustomDatePicker
                        startDate={ymdToDate(customFrom)}
                        setStartDate={(d) => {
                          const next = toDateInput(d)
                          if (customTo && next && next > customTo) {
                            setCustomFrom(customTo)
                            setCustomTo(next)
                            return
                          }
                          setCustomFrom(next)
                        }}
                        placeholder="From"
                        maxDate={ymdToDate(customTo) || undefined}
                      />
                    </Box>
                    <Box w="170px">
                      <CustomDatePicker
                        startDate={ymdToDate(customTo)}
                        setStartDate={(d) => {
                          const next = toDateInput(d)
                          if (customFrom && next && next < customFrom) {
                            setCustomTo(customFrom)
                            setCustomFrom(next)
                            return
                          }
                          setCustomTo(next)
                        }}
                        placeholder="To"
                        minDate={ymdToDate(customFrom) || undefined}
                      />
                    </Box>
                    <Button size="sm" variant="ghost" onClick={clearDashboardDateFilter}>
                      Clear
                    </Button>
                  </>
                ) : dateFilter !== "all_time" ? (
                  <Button size="sm" variant="ghost" onClick={clearDashboardDateFilter}>
                    Clear
                  </Button>
                ) : null}
              </Flex>
              {dateRangeLabel && (
                <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                  {dateRangeLabel}
                </Text>
              )}
            </Box>
          </Flex>
          <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
            <Text fontSize="lg" fontWeight="700" color="#1B2559" mb={4}>
              Performance Overview
            </Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={4}>
              {employeePerformanceCards.map((card, idx) => (
                <Box
                  key={idx}
                  bg="#F8FAFC"
                  borderRadius="12px"
                  p={4}
                  border="1px solid"
                  borderColor="#EDF2F7"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  onClick={card.onClick}
                >
                  <Box w="100%">
                    <Text fontSize="sm" color="#6B7280" fontWeight="500" mb={1}>{card.title}</Text>
                    <Flex align="center" justify="space-between" w="100%">
                      <VStack align="start" spacing={1}>
                        <Text fontSize="30px" fontWeight="700" color={card.valueColor}>{card.value}</Text>
                        <Text fontSize="12px" color="#94A3B8" fontWeight="500">{card.desc}</Text>
                      </VStack>
                      {card.icon && (
                        <Box p={2} borderRadius="xl" bg="white">
                          <Icon as={card.icon} boxSize={dashboardCardIconSize} color={card.valueColor} />
                        </Box>
                      )}
                    </Flex>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Box>

          <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
            <Text fontSize="lg" fontWeight="700" color="#1B2559" mb={4}>
              Hiring Participation
            </Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }} gap={4}>
              {employeeHiringCards.map((items) => (
                <Box
                  key={items.id}
                  bg="#F8FAFC"
                  borderRadius="12px"
                  p={4}
                  border="1px solid"
                  borderColor="#EDF2F7"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                  onClick={items.onClick}
                >
                  <Box>
                    <Text fontSize="sm" color="#6B7280" fontWeight="500" mb={1}>{items.title}</Text>
                    <Text fontSize="30px" fontWeight="700" color="#111827">{items.value}</Text>
                    <Text fontSize="12px" color="#94A3B8" fontWeight="500">{items.desc}</Text>
                  </Box>
                  <Box
                    sx={{
                      "& svg": {
                        width: "28px",
                        height: "28px",
                        display: "block",
                      },
                    }}
                  >
                    {items.image}
                  </Box>
                </Box>
              ))}
            </Grid>
          </Box>

          <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
            {/* <Text fontSize="lg" fontWeight="700" color="#1B2559" mb={4}>
              Today&apos;s Schedule
            </Text> */}
            <Box borderRadius="12px" overflow="hidden">
              <InterviewDetailsCard cardTitle={"Today's Interviews"} showCardDetails={true} data={data} isEmployee={true} />
            </Box>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box py={6} px={6} bg="#F9FAFB" minH="100vh">
      <VStack spacing={6} align="stretch">
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
          <Text fontSize="2xl" fontWeight="700" color="#1B2559">
            {`Welcome, ${userDisplayName} 👋`}
          </Text>
          <Box>
            <Flex align="center" gap={2} flexWrap="wrap" justify="flex-end">
              <Menu closeOnSelect>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" size="sm">
                  {dateFilterLabel}
                </MenuButton>
                <MenuList p={2} minW="240px">
                  {dateFilterOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => setDateFilter(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {dateFilter === "custom" ? (
                <>
                  <Box w="170px">
                    <CustomDatePicker
                      startDate={ymdToDate(customFrom)}
                      setStartDate={(d) => {
                        const next = toDateInput(d)
                        if (customTo && next && next > customTo) {
                          setCustomFrom(customTo)
                          setCustomTo(next)
                          return
                        }
                        setCustomFrom(next)
                      }}
                      placeholder="From"
                      maxDate={ymdToDate(customTo) || undefined}
                    />
                  </Box>
                  <Box w="170px">
                    <CustomDatePicker
                      startDate={ymdToDate(customTo)}
                      setStartDate={(d) => {
                        const next = toDateInput(d)
                        if (customFrom && next && next < customFrom) {
                          setCustomTo(customFrom)
                          setCustomFrom(next)
                          return
                        }
                        setCustomTo(next)
                      }}
                      placeholder="To"
                      minDate={ymdToDate(customFrom) || undefined}
                    />
                  </Box>
                  <Button size="sm" variant="ghost" onClick={clearDashboardDateFilter}>
                    Clear
                  </Button>
                </>
              ) : dateFilter !== "all_time" ? (
                <Button size="sm" variant="ghost" onClick={clearDashboardDateFilter}>
                  Clear
                </Button>
              ) : null}
            </Flex>
            {dateRangeLabel && (
              <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                {dateRangeLabel}
              </Text>
            )}
          </Box>
        </Flex>
        {/* Performance Overview Section */}
        <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
          <Text fontSize="lg" fontWeight="700" color="#1B2559" mb={4}>
            Performance Overview
          </Text>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            {performanceStats.map((card, idx) => (
              <Box
                key={idx}
                className="recruit-info-card"
                bg="#F8FAFC"
                borderRadius="12px"
                p={5}
                border="1px solid"
                borderColor="#EDF2F7"
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
                onClick={card.onClick}
              >
                <Box className="recruit-info">
                  <Box display={"flex"} alignItems={"center"} gap={"12px"}>
                    <Text className="info-card-title" fontSize="sm" color="#6B7280" fontWeight="500" mb={2}>
                      {card.title}
                    </Text>
                  </Box>
                  <Text className="info-card-value" fontSize="32px" fontWeight="700" color="#111827">
                    {card.value}
                  </Text>
                </Box>
                <Box className="info-box-icon">
                  <Box p={2} bg={card.iconBg} borderRadius="xl">
                    <Icon as={card.icon} boxSize={dashboardCardIconSize} color={card.iconColor} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>

        {/* Hiring Participation Section */}
        <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
          <Text fontSize="lg" fontWeight="700" color="#1B2559" mb={4}>
            Hiring Participation
          </Text>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            {FigmaDisplayCards.map((items) => (
              <Box
                key={items.id}
                className="recruit-info-card"
                bg="#F8FAFC"
                borderRadius="12px"
                p={5}
                border="1px solid"
                borderColor="#EDF2F7"
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
                onClick={() => handleCardClick(items.id, items.title)}
              >
                <Box className="recruit-info">
                  <Box display={"flex"} alignItems={"center"} gap={"12px"}>
                    <Text className="info-card-title" fontSize="sm" color="#6B7280" fontWeight="500" mb={2}>
                      {items.title}
                    </Text>
                  </Box>
                  <Text className="info-card-value" fontSize="32px" fontWeight="700" color="#111827">
                    {items.value ?? 0}
                  </Text>
                </Box>
                <Box className="info-box-icon">
                  <Box
                    sx={{
                      "& svg": {
                        width: "28px",
                        height: "28px",
                        display: "block",
                      },
                    }}
                  >
                    {items.image}
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>

        {/* Bottom Section */}
        <Box className="recruit-info-box">
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <GridItem colSpan={2} className="tableptNormal">
              <Box bg="white" borderRadius="12px" boxShadow="0 1px 3px rgba(0,0,0,0.05)" overflow="hidden">
                <InterviewDetailsCard cardTitle={"Open Job Applications"} showTableCard={true} data={data} />
              </Box>
            </GridItem>
            <GridItem colSpan={1}>
              <Box bg="white" borderRadius="12px" boxShadow="0 1px 3px rgba(0,0,0,0.05)" overflow="hidden">
                <InterviewDetailsCard cardTitle={"Today's Interviews"} showCardDetails={true} data={data} />
              </Box>
            </GridItem>
            <GridItem colSpan={1}>
              <Box bg="white" borderRadius="12px" boxShadow="0 1px 3px rgba(0,0,0,0.05)" overflow="hidden">
                <InterviewDetailsCard cardTitle={"Open Jobs"} showCardDetails={true} data={data} />
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* {isAdminOrRecruiter && recentReviews.length > 0 && (
          <Box bg="white" borderRadius="14px" p={5} boxShadow="0 1px 3px rgba(0,0,0,0.05)">
            <Flex justify="space-between" align="center" mb={4}>
              <Text className="cardTitle" fontWeight="600" fontSize="lg" color="#1B2559">Recent Reviews</Text>
              <Button
                variant="ghost"
                size="sm"
                color={brandColor}
                _hover={{ textDecoration: "underline" }}
                fontWeight="500"
                fontSize="14px"
                onClick={() => navigate('/performance/approvals')}
              >
                View All
              </Button>
            </Flex>

            <VStack spacing={4} align="stretch" className="cardBody">
              {recentReviews.map((review) => (
                <Box
                  key={review._id}
                  p={4}
                  bg="#F4F7FE"
                  borderRadius="12px"
                  transition="all 0.2s"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
                  cursor="pointer"
                  onClick={() => navigate('/performance/approvals')}
                >
                  <Flex align="center" justify="space-between">
                    <HStack spacing={3}>
                      <Avatar
                        name={review.employeeName}
                        bg={getAvatarColor(review.employeeName)}
                        color="white"
                        fontWeight="bold"
                        size="md"
                      />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="600" color="#1B2559" fontSize="15px">{review.employeeName}</Text>
                        <Text fontSize="14px" color="#A3AED0">{review.employeeRole}</Text>
                      </VStack>
                    </HStack>

                    <HStack spacing={10}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="12px" fontWeight="700" color="#A3AED0" textTransform="uppercase">Reviewer</Text>
                        <Text fontWeight="600" color="#1B2559" fontSize="14px">{review.reviewerName}</Text>
                      </VStack>

                      <VStack align="start" spacing={0}>
                        <Text fontSize="12px" fontWeight="700" color="#A3AED0" textTransform="uppercase">Submitted</Text>
                        <Text fontWeight="600" color="#1B2559" fontSize="14px">{formatDate(review.submittedDate)}</Text>
                      </VStack>

                      <HStack spacing={4}>
                        <Badge
                          bg={review.status === 'approved' ? "#E6F7E6" : "#FFF4E6"}
                          color={review.status === 'approved' ? "#10B981" : "#FF9900"}
                          borderRadius="4px"
                          px={2}
                          py={0.5}
                          fontSize="12px"
                          fontWeight="500"
                          textTransform="none"
                        >
                          {formatStatus(review.status)}
                        </Badge>
                        <Icon as={MdChevronRight} boxSize={5} color="#A3AED0" />
                      </HStack>
                    </HStack>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </Box>
        )} */}
      </VStack>
    </Box>
  )
}
